import { Link, useLocation } from 'react-router-dom';
import ModalComponent from 'components/Common/Modal';
import { useAuth0 } from '@auth0/auth0-react';
import useTopicOrClientWord from 'components/Dashboard/hooks/useTopicOrClientWord';
import { useContext, useEffect, useRef, useState } from 'react';
import { store } from 'components/Store';
import getUrlParam from 'components/Dashboard/utilities/getUrlParam';
import useReportIconAutoselect from 'components/Reports/useReportIconAutoselect';
import data from '@emoji-mart/data';
import { init } from 'emoji-mart';
import returnCorrectWPUrl from 'components/Dashboard/utilities/returnCorrectWPUrl';
import getAppSite, { comprehensiveText } from 'utils/getAppSite';

const NotFeaturePopup = (props) => {
  const {
    children,
    leftTitle,
    listItems,
    hideDemo,
    linkProp,
    noButtons,
    hideLeftSide,
    noHighlightFirstItem,
    ...modalProps
  } = props;
  const { isAuthenticated } = useAuth0();

  return (
    <ModalComponent
      {...modalProps}
      overlayClassName={modalProps?.overlayClassName ?? 'not-features-popup react-modal-overlay adjustment-of-position'}
      style={{ overflow: 'auto' }}
    >
      <div className='bg-white rounded-lg relative'>
        <div className='d-lg-flex'>
          {!hideLeftSide && (
            <div className='bg-main-blue py-5 text-left rounded-left popup-left-side'>
              <h3 className='title-h3 pl-4 pl-lg-5 report-prospecting-title'>
                <img
                  src={`${import.meta.env.VITE_APP_CDNURL}/images/logo_white.svg`}
                  alt='Logo'
                  className='policy-logo mr-2'
                />
                {leftTitle ? leftTitle : isAuthenticated ? 'Mogul plan' : ''}
              </h3>
              {listItems?.length > 0 && (
                <ul className='policy-list text-white text-left title-h5-m py-4 mt-4 statistics-popup-list px-3 pl-lg-5 pr-lg-0'>
                  {listItems
                    .filter((item) => !item.hidden)
                    .map((item, i) => {
                      return (
                        <li
                          key={`item-${i}-listItem`}
                          className={`pl-4 mb-3 py-2 nowrap-item ${
                            i === 0 && !noHighlightFirstItem
                              ? 'bg-white text-main-blue statistics-list-active-item'
                              : ''
                          } `}
                        >
                          <span className='inline-block title-h3 mr-3 icon-small-check' />
                          {item.label}
                        </li>
                      );
                    })}
                </ul>
              )}
            </div>
          )}
          <div className='grow px-5 pb-5 mb-3'>
            {children}

            {!noButtons && (
              <div className='nowrap-item text-lg-right pt-5 mt-5 d-lg-flex align-items-center'>
                {!hideDemo && (
                  <a
                    href={getAppSite() === 'usa' ? '/' : `${returnCorrectWPUrl()}/demo`}
                    target='_blank'
                    rel='noreferrer'
                    onClick={(e) => {
                      if (isAuthenticated) {
                        e.preventDefault();
                        if (modalProps?.setIsOpen) modalProps?.setIsOpen(false);
                      }
                    }}
                    className={`downgrade-pricing-button btn btn-general outline-active-button rounded-button px-3 px-xxl-5 py-2 mr-0 mr-lg-3 title-h5-m ${
                      isAuthenticated ? 'contact-button-launch-messenger' : ''
                    }`}
                  >
                    {isAuthenticated ? 'Contact us' : 'Arrange demo'}
                  </a>
                )}
                {linkProp && (
                  <Link
                    className='downgrade-pricing-button mt-3 mt-lg-0 action-button rounded-button py-2 px-3 px-xxl-4 d-inline-block title-h5-m text-center'
                    to={linkProp.to}
                    onClick={(e) => {
                      if (linkProp.onClick) {
                        linkProp.onClick(e);
                      }
                    }}
                  >
                    <span className='px-4'>
                      {linkProp.label ? linkProp.label : isAuthenticated ? 'Compare plans' : 'Start your free trial'}
                      <span className='icon-long-arrow-right paragraph-p5 inline-block ml-2 ml-lg-3' />
                    </span>
                  </Link>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </ModalComponent>
  );
};

const AnalyticsPopup = (props) => {
  const globalState = useContext(store);
  const { state, dispatch } = globalState;
  const { analyticsState } = state;
  const { popupTitle, sideBarText, useEmoji } = analyticsState ?? {};
  const { isOpen, setIsOpen } = props;
  const { isAuthenticated } = useAuth0();
  const { callLoggedOutIconSearch } = useReportIconAutoselect();

  const [icon, setIcon] = useState();
  const callUseEmoji = useRef(null);
  callUseEmoji.current = async () => {
    let value = await callLoggedOutIconSearch({ value: getUrlParam('search') });
    if (value) {
      setIcon(value);
    }
  };
  useEffect(() => {
    if (useEmoji) {
      callUseEmoji.current();
    }
  }, [useEmoji]);

  useEffect(() => {
    init({ data });
  }, []);

  const usUnAuthenticatedCondition = getAppSite() === 'usa' && !isAuthenticated;

  return (
    <NotFeaturePopup
      hideDemo={usUnAuthenticatedCondition}
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      maxWidth={1000}
      overlayClassName='react-modal-overlay report-extra-index-modal not-features-popup'
      onAfterClose={() => {
        setIcon(null);
        let newState = {
          ...analyticsState,
          popupTitle: null,
        };
        dispatch({
          type: 'MODIFY_SECTION',
          parameter: 'analyticsState',
          value: newState,
        });
      }}
      noHighlightFirstItem={usUnAuthenticatedCondition}
      listItems={[
        { label: sideBarText ?? (usUnAuthenticatedCondition ? 'Unlimited searches' : 'Analytics') },
        {
          label: isAuthenticated
            ? 'Reports'
            : getAppSite() === 'usa'
              ? 'Advanced rules'
              : 'Tailored political monitoring',
        },
        {
          label: isAuthenticated ? 'Dynamic groups' : getAppSite() === 'usa' ? 'Save searches' : 'Stakeholder mapping',
        },
        {
          label: isAuthenticated
            ? 'Dedicated account manager'
            : getAppSite() === 'usa'
              ? 'Embed charts'
              : 'Full policymaker CRM',
        },
        {
          label: getAppSite() === 'usa' ? 'Drill into the contributions' : 'Lobbying platform',
          hidden: isAuthenticated,
        },
      ]}
      linkProp={{
        to: `/${isAuthenticated ? 'pricing' : 'register?reg_source=data-hub-page'}`,
        label: isAuthenticated
          ? 'Compare plans'
          : usUnAuthenticatedCondition
            ? 'Buy Data Hub'
            : 'Try PolicyMogul for free',
      }}
    >
      <p className='title-h2 mt-3 pt-3 pt-lg-5 mb-4 flex-centered'>
        <span className='font-weight-bold'>{popupTitle ?? 'Political data that’s off the charts'}</span>
        {!!icon && (
          <span className='report-list-icon text-center d-inline-block-centered ml-2'>
            <em-emoji set={'apple'} id={icon} size={'1.3em'} style={{ lineHeight: 1 }} />
          </span>
        )}
      </p>
      <div className='title-h4 main-subtle-text'>
        <p>
          {usUnAuthenticatedCondition ? 'Data Hub' : 'Our analytics platform'} gives you instant access to the data that
          will transform your public affairs and campaigning work.
        </p>
        <p>
          {isAuthenticated ? (
            <>
              You’ll be able to automatically identify trends for any topic, including sentiment analysis, based on
              political party, region, parliamentary position and more.
            </>
          ) : (
            <>
              {usUnAuthenticatedCondition ? 'Paying subscribers' : 'Registered users'} get more charts, unlimited
              searches, advanced rules, embed options, and detailed insights into specific contributions.
            </>
          )}
        </p>
      </div>
    </NotFeaturePopup>
  );
};

const ReportsPopup = (props) => {
  const { isOpen, setIsOpen } = props;
  const { isAuthenticated } = useAuth0();
  const { agencyUser } = useTopicOrClientWord();

  return (
    <NotFeaturePopup
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      maxWidth={980}
      listItems={[
        { label: 'Reports' },
        { hidden: isAuthenticated, label: 'Tailored political monitoring' },
        { label: isAuthenticated ? 'Parliament live' : 'Analytics on any policy area' },
        { label: isAuthenticated ? 'Dynamic groups' : 'Advanced stakeholder mapping' },
        { label: isAuthenticated ? 'Dedicated account manager' : 'Policymaker CRM' },
      ]}
      linkProp={{
        to: isAuthenticated ? '/pricing' : '/register?utm_source=report-from-update-button',
      }}
    >
      <p className='title-h2-bold mt-3 pt-3 pt-lg-5 mb-4'>
        {isAuthenticated
          ? `Reports are available on the ${agencyUser ? 'Enterprise' : 'Mogul'} plan`
          : `You’ll need an account to create reports`}
      </p>
      <div className='title-h4 main-subtle-text'>
        <p>
          PolicyMogul’s report-builder allows you to quickly and easily create PDF reports in relation to any issue or
          political update.
        </p>

        <p>
          Create reports from scratch, or use one of our templates to showcase the most relevant political updates,
          stakeholders and analysis for any issue.
        </p>
      </div>
    </NotFeaturePopup>
  );
};

const DataPopup = (props) => {
  const { isOpen, setIsOpen } = props;
  const { isAuthenticated } = useAuth0();

  return (
    <NotFeaturePopup
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      maxWidth={1150}
      leftTitle={<></>}
      listItems={[
        { label: 'Real-time political data' },
        { label: 'Tailored political monitoring' },
        { label: 'Stakeholder mapping' },
        { label: 'Full policymaker CRM' },
        { label: 'Lobbying platform' },
      ]}
      linkProp={{
        to: isAuthenticated ? '/pricing' : '/register?utm_source=sentiment-page-try-policymogul-button',
      }}
      hideDemo={true}
    >
      <p className='title-h2-bold mt-3 pt-3 pt-lg-5 pb-4 mb-4 text-center'>
        PolicyMogul is an all-in-one public affairs platform powered by {comprehensiveText()} political monitoring
        service
      </p>
      <div className='title-h4 main-subtle-text'>
        <img
          className='mx-auto d-block mb-4'
          src={`${import.meta.env.VITE_APP_CDNURL}/images/20230404-data-screenshot.png`}
          alt={'data screenshot'}
        />
      </div>
    </NotFeaturePopup>
  );
};

const AIAssistantPopup = (props) => {
  const { isOpen, setIsOpen } = props;

  return (
    <NotFeaturePopup
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      maxWidth={980}
      listItems={[
        { label: 'AI assistant' },
        { label: 'Analytics on any policy area' },
        { label: 'Advanced stakeholder mapping' },
        { label: 'Policymaker CRM' },
      ]}
      linkProp={{
        to: '/register?utm_source=ai-assistant-widget',
      }}
    >
      <p className='title-h2-bold mt-3 pt-3 pt-lg-5 mb-4'>{`You’ll need an account to use the AI assistant`}</p>
      <div className='title-h4 main-subtle-text'>
        <p>
          PolicyMogul’s AI assistant allows you to quickly and easily get answers to any question in relation to a
          political update.
        </p>
        <p>
          Ask questions about the nature of the update itself or its relevance to your work, and uncover actionable
          insights to inform your public affairs strategy and decision-making.
        </p>
      </div>
    </NotFeaturePopup>
  );
};

const CreateAlertPopup = (props) => {
  const { isOpen, setIsOpen } = props;
  const { isAuthenticated } = useAuth0();

  return (
    <NotFeaturePopup
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      maxWidth={980}
      listItems={[
        { label: 'Real-time alerts' },
        { hidden: isAuthenticated, label: 'Tailored political monitoring' },
        { label: isAuthenticated ? 'Analytics' : 'Analytics on any policy area' },
        { label: isAuthenticated ? 'Dynamic groups' : 'Advanced stakeholder mapping' },
        { label: isAuthenticated ? 'Dedicated account manager' : 'Policymaker CRM' },
      ]}
      linkProp={{
        to: isAuthenticated ? '/pricing' : '/register?utm_source=report-from-update-button',
      }}
    >
      <p className='title-h2-bold mt-3 pt-3 pt-lg-5 mb-4'>Get live updates from Parliament</p>
      <div className='title-h4 main-subtle-text'>
        <p>
          PolicyMogul's real-time alerts make sure you're the first to know when your organisation or interests are
          mentioned in Parliament.
        </p>

        <p>
          Alerts are bundled every 15 minutes, ensuring you stay informed without being overwhelmed, and transcripts can
          be followed in real-time directly from our platform.
        </p>
      </div>
    </NotFeaturePopup>
  );
};

const SearchEventPopup = (props) => {
  const { isOpen, setIsOpen } = props;
  const { isAuthenticated } = useAuth0();
  return (
    <>
      <NotFeaturePopup
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        maxWidth={980}
        listItems={[
          { label: 'See all relevant mentions' },
          { hidden: isAuthenticated, label: 'Tailored political monitoring' },
          { label: isAuthenticated ? 'Analytics' : 'Analytics on any policy area' },
          { label: isAuthenticated ? 'Dynamic groups' : 'Advanced stakeholder mapping' },
          { label: isAuthenticated ? 'Dedicated account manager' : 'Policymaker CRM' },
        ]}
        linkProp={{
          to: isAuthenticated ? '/pricing' : '/register?utm_source=report-from-update-button',
        }}
      >
        <p className='title-h2-bold mt-3 pt-3 pt-lg-5 mb-4'>Get live updates from Parliament</p>
        <div className='title-h4 main-subtle-text'>
          <p>
            PolicyMogul's real-time alerts make sure you're the first to know when your organisation or interests are
            mentioned in Parliament.
          </p>

          <p>
            Alerts are bundled every 15 minutes, ensuring you stay informed without being overwhelmed, and transcripts
            can be followed in real-time directly from our platform.
          </p>
        </div>
      </NotFeaturePopup>
    </>
  );
};

const ParliamentLivePopup = (props) => {
  const { isOpen, setIsOpen } = props;
  const { isAuthenticated } = useAuth0();
  const { agencyUser } = useTopicOrClientWord();

  return (
    <NotFeaturePopup
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      overlayClassName={'react-modal-overlay not-features-popup'}
      maxWidth={980}
      listItems={[
        { label: 'Parliament live' },
        { label: 'Reports' },
        { label: isAuthenticated ? 'Dynamic groups' : 'Advanced stakeholder mapping' },
        { label: isAuthenticated ? 'Sentiment analysis' : 'Analytics on any policy area' },
      ]}
      linkProp={{
        to: '/register?utm_source=ai-assistant-widget',
      }}
    >
      <p className='title-h2-bold mt-3 pt-3 pt-lg-5 mb-4'>
        {isAuthenticated
          ? `Parliament live is available on the ${agencyUser ? 'Enterprise' : 'Mogul'} plan`
          : `Get live updates from Parliament`}
      </p>
      <div className='title-h4 main-subtle-text'>
        <p>
          Parliament live gives you real-time transcription and alerts from the Commons, Lords, Westminster Hall and
          committees.
        </p>
        <p>
          You no longer need to wait for Hansard or official reports to know when your organisation or key interests are
          mentioned in Parliament.
        </p>
      </div>
    </NotFeaturePopup>
  );
};

const SentimentPopup = (props) => {
  const { isOpen, setIsOpen } = props;
  const { isAuthenticated } = useAuth0();

  return (
    <NotFeaturePopup
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      maxWidth={1000}
      overlayClassName='react-modal-overlay report-extra-index-modal not-features-popup'
      listItems={[
        { label: 'Sentiment analysis' },
        { label: isAuthenticated ? 'Reports' : 'Tailored political monitoring' },
        { label: isAuthenticated ? 'Dynamic groups' : 'Stakeholder mapping' },
        { label: isAuthenticated ? 'Dedicated account manager' : 'Full policymaker CRM' },
        { label: 'Lobbying platform', hidden: isAuthenticated },
      ]}
      linkProp={{
        to: `/${isAuthenticated ? 'pricing' : 'register?reg_source=data-hub-page'}`,
        label: isAuthenticated ? 'Compare plans' : 'Try PolicyMogul for free',
      }}
    >
      <p className='title-h2 mt-3 pt-3 pt-lg-5 mb-4 flex-centered'>
        <span className='font-weight-bold'>Track political sentiment on any issue</span>
      </p>
      <div className='title-h4 main-subtle-text'>
        <p>
          Our sentiment analysis features give you instant insight into how policymakers are speaking in relation to any
          issue over time.
        </p>
        <p>
          You’ll be able to track changes in tone and identify sentiment trends by party, region, parliamentary position
          and more.
        </p>
      </div>
    </NotFeaturePopup>
  );
};

const DisabledTwitterPopup = (props) => {
  const { isOpen, setIsOpen } = props;
  const { isAuthenticated } = useAuth0();

  const usUnAuthenticatedCondition = getAppSite() === 'usa' && !isAuthenticated;

  return (
    <NotFeaturePopup
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      hideDemo={usUnAuthenticatedCondition}
      maxWidth={isAuthenticated ? 520 : 1000}
      overlayClassName='react-modal-overlay report-extra-index-modal not-features-popup'
      hideLeftSide={isAuthenticated}
      noHighlightFirstItem={usUnAuthenticatedCondition}
      listItems={[
        { label: usUnAuthenticatedCondition ? 'Unlimited searches' : 'Analytics' },
        {
          label: isAuthenticated
            ? 'Reports'
            : getAppSite() === 'usa'
              ? 'Advanced rules'
              : 'Tailored political monitoring',
        },
        {
          label: isAuthenticated ? 'Dynamic groups' : getAppSite() === 'usa' ? 'Save searches' : 'Stakeholder mapping',
        },
        {
          label: isAuthenticated
            ? 'Dedicated account manager'
            : getAppSite() === 'usa'
              ? 'Embed charts'
              : 'Full policymaker CRM',
        },
        {
          label: getAppSite() === 'usa' ? 'Drill into the contributions' : 'Lobbying platform',
          hidden: isAuthenticated,
        },
      ]}
      linkProp={{
        to: `/register?reg_source=data-hub-page`,
        label: usUnAuthenticatedCondition ? 'Buy Data Hub' : 'Try PolicyMogul for free',
      }}
      noButtons={isAuthenticated}
    >
      <p className='title-h2 mt-3 pt-3 pt-lg-5 mb-4 flex-centered'>
        <span className='font-weight-bold'>Track what policymakers are posting</span>
      </p>
      <div className='title-h4 main-subtle-text'>
        <p>PolicyMogul’s X monitoring features let you see what policymakers are saying publicly, in real time.</p>
        {isAuthenticated && (
          <p>
            Available as an add-on.{' '}
            <span
              className='link-text text-light-blue hover-opacity-85 contact-button-launch-messenger'
              onClick={() => {
                setIsOpen(false);
              }}
            >
              Contact us
            </span>{' '}
            to enable X monitoring.
          </p>
        )}
      </div>
    </NotFeaturePopup>
  );
};

const AIStakeholderSummariesPopup = (props) => {
  const { isOpen, setIsOpen } = props;

  return (
    <NotFeaturePopup
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      maxWidth={1000}
      overlayClassName='react-modal-overlay report-extra-index-modal not-features-popup'
      listItems={[
        { label: 'AI summaries' },
        { label: 'Tailored political monitoring' },
        { label: 'Stakeholder mapping' },
        { label: 'Full policymaker CRM' },
        { label: 'Lobbying platform' },
      ]}
      linkProp={{
        to: `/register?reg_source=data-hub-page'}`,
        label: 'Try PolicyMogul for free',
      }}
    >
      <p className='title-h2 mt-3 pt-3 pt-lg-5 mb-4 flex-centered'>
        <span className='font-weight-bold'>Summarise any stakeholder’s stance</span>
      </p>
      <div className='title-h4 main-subtle-text'>
        <p>
          AI summaries give you instant clarity on stakeholders’ interests and stances. They draw on details from their
          biography as well as recent contributions for each issue.
        </p>
      </div>
    </NotFeaturePopup>
  );
};

const DataHubNavigationPopup = (props) => {
  const globalState = useContext(store);
  const { state, dispatch } = globalState;
  const { dataHubNavigationPopup } = state ?? {};
  const { isOpen } = dataHubNavigationPopup;

  const setIsOpen = (value, forceClose = false) => {
    if (dataHubNavigationPopup.allowClose || forceClose) {
      dispatch({
        type: 'MODIFY_SECTION',
        parameter: 'dataHubNavigationPopup',
        value: { ...dataHubNavigationPopup, isOpen: value },
      });
    }
  };

  const location = useLocation();

  const sections = [
    { index: 0, label: 'Data Hub', path: '/analytics/dashboard' },
    {
      index: 1,
      label: 'Real-time monitoring',
      content: [
        'Track political and policy developments as they happen, with updates from across Congress, the federal government, states, regulators, and more.',
        'Our political monitoring platform also includes an AI assistant to help contextualize any update, and the ability to a feed of live updates on any website.',
      ],
      path: '/monitor-inbox',
      pathAliases: ['/settings'],
    },
    {
      index: 2,
      label: 'Staffer CRM',
      content: [
        'Our policymaker and staffer CRM helps you manage relationships with elected officials, staff, and other key contacts in one place.',
        'Filter contacts in dozens of ways, including by issue area, sentiment, state, party, office, and custom tags. You can also create self-updating groups and send tracked email campaigns directly to staffers.',
      ],
      path: '/people/directory',
    },
    {
      index: 3,
      label: 'Stakeholder mapping',
      content: [
        'Quickly identify the policymakers and staffers most relevant to your priorities.',
        'See who is shaping the conversation on the issues that matter to you, understand their sentiment and position on a given topic, and filter by role, party, state, committee, and other criteria to build smarter engagement strategies and targeted outreach lists.',
      ],
      path: '/people/stakeholder-mapping',
      pathAliases: ['/stakeholders'],
    },
    {
      index: 4,
      label: 'Report-builder',
      content: [
        'Create polished, branded reports in just a couple of clicks. Reports can include full updates or summaries, analytics, custom commentary, relevant stakeholders and more.',
        'This feature can be used to create briefings, internal updates, board reports, and policy snapshots in minutes, all automatically populated with the most relevant live intelligence from across the platform.',
      ],
      path: '/reports',
    },
  ];
  const getSectionByPathname = (pathname) => {
    const firstSegment = pathname?.split('/').filter(Boolean)?.[0];
    const mainPath = firstSegment ? `/${firstSegment}` : '';

    return sections.find((section) => {
      const roots = [section.path, ...(section.pathAliases ?? [])];
      return roots.some(
        (root) =>
          root === pathname || pathname.startsWith(`${root}/`) || root === mainPath || root.startsWith(`${mainPath}/`)
      );
    });
  };

  const firstPublicAffairsSection = sections[1];
  const getInitialSelectedSection = () => {
    const matchingSection = getSectionByPathname(location.pathname);
    if (!matchingSection || matchingSection.index === 0) return firstPublicAffairsSection;
    return matchingSection;
  };

  const [selectedSection, setSelectedSection] = useState(getInitialSelectedSection);

  useEffect(() => {
    const matchingSection = getSectionByPathname(location.pathname);
    if (typeof matchingSection?.index === 'number' && matchingSection.index !== 0) {
      setSelectedSection(matchingSection);
    } else {
      setSelectedSection(firstPublicAffairsSection);
    }
  }, [location.pathname, isOpen]);

  return (
    <ModalComponent
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      overlayClassName={'not-features-popup react-modal-overlay adjustment-of-position'}
      style={{ overflow: 'auto' }}
      maxWidth={980}
      notShowCloseButton={!dataHubNavigationPopup.allowClose}
      onAfterClose={() => {
        //OP: make it initial state after close
        dispatch({
          type: 'MODIFY_SECTION',
          parameter: 'dataHubNavigationPopup',
          value: { isOpen: false },
        });
      }}
    >
      <div className='bg-white rounded-lg relative'>
        <div className='d-lg-flex'>
          <div className='bg-main-blue py-5 text-left rounded-left popup-left-side'>
            <h3 className='title-h4 pl-4 pl-lg-5 report-prospecting-title'>
              <img
                src={`${import.meta.env.VITE_APP_CDNURL}/images/logo_white.svg`}
                alt='Logo'
                className='policy-logo mr-2'
              />
              <span className='ml-2 d-inline-block' style={{ transform: 'translateY(2px)' }}>
                Public Affairs Suite
              </span>
            </h3>
            <ul className='policy-list text-white text-left title-h5-m py-4 mt-4 statistics-popup-list px-3 pl-lg-5 pr-lg-0'>
              {sections.map((item, i) => {
                return (
                  <li
                    key={`item-${i}-listItem`}
                    className={`pl-4 mb-3 py-2 nowrap-item ${item.index !== 0 ? 'pointer' : ''} ${
                      selectedSection?.index === item.index ? 'bg-white text-main-blue statistics-list-active-item' : ''
                    } `}
                    onClick={() => {
                      if (item.index !== 0) setSelectedSection(item);
                    }}
                  >
                    <span className='inline-block title-h3 mr-3 icon-small-check' />
                    {item.label}
                  </li>
                );
              })}
            </ul>
          </div>
          <div className='grow px-5 pb-5'>
            <div className='d-flex flex-column h-100 justify-content-between'>
              <div>
                <p className='title-h2-bold mt-3 pt-3 pt-lg-5 mb-4'>{selectedSection?.label}</p>
                <div className='title-h4 main-subtle-text'>
                  {selectedSection?.content?.map((content, i) => {
                    return <p key={`item-${i}-content`}>{content}</p>;
                  })}
                </div>
              </div>
              <div className='d-lg-flex align-items-center'>
                {!dataHubNavigationPopup.allowClose && (
                  <Link
                    className='downgrade-pricing-button mr-0 mr-lg-3 action-button rounded-button py-2 px-3 px-xxl-4 d-inline-block title-h5-m text-center'
                    to={'/analytics/dashboard'}
                    onClick={(e) => {
                      setIsOpen(false, true);
                    }}
                  >
                    <span className='px-4'>
                      <span className='icon-long-arrow-left paragraph-p5 inline-block mr-2 mr-lg-3' />
                      Back to Data Hub
                    </span>
                  </Link>
                )}
                <a
                  href={`${returnCorrectWPUrl()}/demo`}
                  target='_blank'
                  rel='noreferrer'
                  className={`downgrade-pricing-button mt-3 mt-lg-0 btn btn-general outline-active-button rounded-button px-3 px-xxl-5 py-2 title-h5-m`}
                >
                  Book a demo
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ModalComponent>
  );
};

export {
  AnalyticsPopup,
  ReportsPopup,
  DataPopup,
  AIAssistantPopup,
  ParliamentLivePopup,
  CreateAlertPopup,
  SearchEventPopup,
  SentimentPopup,
  AIStakeholderSummariesPopup,
  DisabledTwitterPopup,
  DataHubNavigationPopup,
};
